import { Injectable } from '@angular/core';
import { Http } from 'src/app/shared/services/http.service';
import { LogApi, LogMessage } from '..';
import {
  ChatBotScriptLoaderService,
  WebchatApi,
} from './chatBotScriptLoader.service';

const CHAT_CONNECT_TIMEOUT_MS = 20000;
const CHAT_CONNECT_POLL_MS = 250;

interface ChatInitResponse {
  username: string;
  application: string;
  clientid: string;
  lob: string;
  sor: string;
  claimId: string;
  language: string;
  baseApiUrl: string;
  claimData?: unknown;
  conversationId?: string;
  userType?: string;
  isDelegating?: string;
  liveChat?: string;
  careyChat?: string;
}

interface CareyTokenPayload {
  username: string;
  application: string;
  clientid: string;
  lob: string;
  sor: string;
  claimId: string;
  language: string;
  userType?: string;
  isDelegating: string;
  liveChat: string;
  careyChat: string;
}

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private loading: boolean = false;

  private chatInit: ChatInitResponse | null = null;

  private source: string;

  private claimId: string;

  private lob: string;

  constructor(
    private http: Http,
    private logApi: LogApi,
    private chatBotScriptLoaderService: ChatBotScriptLoaderService,
  ) {}

  private get webchat(): WebchatApi | undefined {
    return this.chatBotScriptLoaderService.getWebchat();
  }

  isConversationOn(): boolean {
    return this.webchat?.isConversationOn?.() === true;
  }

  async initialize(
    source: string,
    claimId: string,
    lob: string,
    userOwns: boolean,
  ): Promise<boolean> {
    this.loading = true;
    this.source = source;
    this.claimId = claimId;
    this.lob = lob;

    try {
      if (this.chatInit) {
        this.onDestroy();
      }

      await this.chatBotScriptLoaderService.ensureChatReady();
      const chat = this.webchat;
      if (!chat) {
        throw new Error('Chat is unavailable because it failed to load.');
      }

      this.chatInit = await this.http.get<ChatInitResponse>(
        `/api/chat/${source}/${claimId}/${lob}/${userOwns}/token`,
        { cache: false, timeout: 1000 * 60 },
      );

      const languagePreference = sessionStorage.getItem('languagepreference');
      this.chatInit.language = languagePreference
        ? languagePreference.toUpperCase()
        : 'EN';

      chat.config({
        webchat: {
          baseApiUrl: this.chatInit.baseApiUrl,
        },
      });

      const dataForTokenGeneration = this.buildTokenGenerationPayload();

      chat.initChat(dataForTokenGeneration, this.chatInit?.claimData);
      await this.waitForConversation(chat);
      return true;
    } catch (e: unknown) {
      this.logChatError(e);
    } finally {
      this.loading = false;
    }
  }

  private onDestroy(): void {
    this.chatInit = null;
  }

  async close() {
    if (this.chatInit) {
      await this.webchat?.endConversation();
      this.chatInit = null;
    }
    this.loading = false;
    return true;
  }

  isActive(): boolean {
    return this.loading || !!document.getElementById('chatcontainer');
  }

  private async waitForConversation(chat: WebchatApi): Promise<void> {
    const deadline = Date.now() + CHAT_CONNECT_TIMEOUT_MS;

    while (Date.now() < deadline) {
      if (chat.isConversationOn?.() === true) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, CHAT_CONNECT_POLL_MS));
    }

    throw new Error(
      `Chat did not connect within ${CHAT_CONNECT_TIMEOUT_MS}ms of initChat.`,
    );
  }

  private buildTokenGenerationPayload(): CareyTokenPayload {
    if (!this.chatInit) {
      throw new Error('Chat session is not initialized.');
    }

    return {
      username: this.chatInit.username,
      application: this.chatInit.application,
      clientid: this.chatInit.clientid,
      lob: this.chatInit.lob,
      sor: this.chatInit.sor,
      claimId: this.chatInit.claimId,
      language: this.chatInit.language,
      userType: this.chatInit.userType,
      isDelegating: this.chatInit.isDelegating ?? 'N',
      liveChat: this.chatInit.liveChat ?? 'N',
      careyChat: this.chatInit.careyChat ?? 'N',
    };
  }

  private logChatError(e: unknown): never {
    const detail = e instanceof Error ? e.message : String(e);

    if (!detail.includes('Http failure')) {
      const message = new LogMessage();
      message.message = `message: Error,\n conversationId: ${
        this.chatInit?.conversationId
      },\n claimId: ${this.claimId},\n source: ${
        this.source
      },\n message: ${detail.replaceAll(':', '')}`;
      this.logApi.log(message, 'Error');
    }
    throw new Error('Unable to open chat at this time');
  }
}
